import React, { useEffect, useRef } from 'react';

/**
 * Canvas-based continuous confetti rain. Recycles particles when they fall off
 * the bottom, so animation runs indefinitely with constant memory.
 *
 * Props:
 *   - count: number of particles (default 150)
 *   - className: extra css for the canvas element
 */
export const ConfettiCanvas = ({ count = 150, className = '' }) => {
  const canvasRef = useRef(null);
  const rafRef = useRef(0);
  const particlesRef = useRef([]);

  useEffect(() => {
    // Respect reduced-motion preference — skip animation entirely
    if (typeof window !== 'undefined' &&
        window.matchMedia &&
        window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
      return;
    }

    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');

    const COLORS = ['#FF3B6B', '#FF7A2E', '#FFD166', '#06D6A0', '#6366F1', '#FFB5D8', '#B5FFFC'];

    const createParticle = (w, h, fromTop = false) => {
      const shape = Math.random() < 0.5 ? 'rect' : 'circle';
      return {
        x: Math.random() * w,
        y: fromTop ? -20 - Math.random() * h : Math.random() * h,
        size: 4 + Math.random() * 6,
        color: COLORS[(Math.random() * COLORS.length) | 0],
        speedY: 0.6 + Math.random() * 1.6,
        speedX: (Math.random() - 0.5) * 0.8,
        rotation: Math.random() * Math.PI * 2,
        rotationSpeed: (Math.random() - 0.5) * 0.08,
        shape,
        opacity: 0.55 + Math.random() * 0.35,
      };
    };

    const resize = () => {
      const parent = canvas.parentElement;
      const w = parent ? parent.clientWidth : window.innerWidth;
      const h = parent ? parent.clientHeight : window.innerHeight;
      const dpr = Math.min(window.devicePixelRatio || 1, 2);
      canvas.width = w * dpr;
      canvas.height = h * dpr;
      canvas.style.width = `${w}px`;
      canvas.style.height = `${h}px`;
      ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
    };

    const initParticles = (w, h) => {
      particlesRef.current = Array.from({ length: count }, () => createParticle(w, h, false));
    };

    resize();
    const w = canvas.clientWidth, h = canvas.clientHeight;
    initParticles(w, h);

    const draw = () => {
      const w2 = canvas.clientWidth, h2 = canvas.clientHeight;
      ctx.clearRect(0, 0, w2, h2);
      const parts = particlesRef.current;
      for (let i = 0; i < parts.length; i++) {
        const p = parts[i];
        p.x += p.speedX;
        p.y += p.speedY;
        p.rotation += p.rotationSpeed;
        if (p.y > h2 + 20 || p.x < -30 || p.x > w2 + 30) {
          Object.assign(p, createParticle(w2, h2, true));
          continue;
        }
        ctx.save();
        ctx.globalAlpha = p.opacity;
        ctx.translate(p.x, p.y);
        ctx.rotate(p.rotation);
        ctx.fillStyle = p.color;
        if (p.shape === 'rect') {
          ctx.fillRect(-p.size / 2, -p.size / 3, p.size, p.size * 0.66);
        } else {
          ctx.beginPath();
          ctx.arc(0, 0, p.size / 2, 0, Math.PI * 2);
          ctx.fill();
        }
        ctx.restore();
      }
      rafRef.current = requestAnimationFrame(draw);
    };
    rafRef.current = requestAnimationFrame(draw);

    const onResize = () => {
      resize();
    };
    window.addEventListener('resize', onResize);
    return () => {
      cancelAnimationFrame(rafRef.current);
      window.removeEventListener('resize', onResize);
    };
  }, [count]);

  return <canvas ref={canvasRef} className={`cb-confetti-canvas ${className}`} aria-hidden="true" data-testid="hero-confetti-canvas" />;
};

export default ConfettiCanvas;
